/**
 * Pokémon sprite helpers — single source of truth for sprite URLs.
 *
 * Two build-time env vars control behavior:
 *
 *   VITE_ENABLE_SPRITES  ("true" | "false", default "true")
 *     Global kill switch.  Set to "false" + redeploy to drop all
 *     sprites from the UI within ~2 minutes — the response path if
 *     Nintendo ever sends a takedown notice for our hotlinked
 *     copyrighted assets.  All consumers go through spritesEnabled()
 *     so flipping this hides every sprite uniformly.
 *
 *   VITE_SPRITE_SOURCE   ("PokeAPI" | "LeekDuck", default "PokeAPI")
 *     Switches the URL pattern for COMPUTED sprite URLs (Pokémon-stat
 *     cards that build a URL from pokedex_id).  Does NOT affect the
 *     ScrapedDuck cards (raids/eggs/research/CDs) — those use the
 *     authoritative URL ScrapedDuck published in the data feed itself,
 *     which lives in `raid_boss.image` etc.  Overriding those would
 *     mean second-guessing the data source.
 *
 *     PokeAPI:  classic 96×96 pixel art via raw.githubusercontent.com.
 *               Very stable URLs (GitHub raw, basically forever).
 *     LeekDuck: Pokémon GO 3D-render style via cdn.leekduck.com.
 *               Matches the in-game look; URL paths controlled by a
 *               single fan-run site (could change).
 *
 * Both sprite sources are unlicensed redistributions of Nintendo /
 * Pokémon Company / Game Freak copyrighted assets — broadly tolerated
 * for non-commercial fan informational use but not formally permitted.
 * VITE_ENABLE_SPRITES exists so we can flip them off immediately if
 * that tolerance ever expires.
 */

/** Sprite kill-switch.  Defaults to enabled; flip via Cloudflare Pages
 * env var + redeploy when needed. */
export function spritesEnabled(): boolean {
  // Vite inlines import.meta.env values at build time.  Anything other
  // than the literal string "false" (case-insensitive) keeps sprites
  // on so a typo or missing env var doesn't accidentally hide them.
  const raw = import.meta.env.VITE_ENABLE_SPRITES;
  if (typeof raw !== "string") return true;
  return raw.toLowerCase() !== "false";
}

type SpriteSource = "pokeapi" | "leekduck";

/** Resolve the configured source name.  Defaults to PokeAPI. */
export function spriteSource(): SpriteSource {
  const raw = import.meta.env.VITE_SPRITE_SOURCE;
  if (typeof raw === "string" && raw.toLowerCase() === "leekduck") {
    return "leekduck";
  }
  return "pokeapi";
}

/** Build a sprite URL for a given Pokédex ID.
 *
 *   pokeapi  → https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/{id}.png
 *   leekduck → https://cdn.leekduck.com/assets/img/pokemon_icons_crop/pm{id}.icon.png
 *
 * Returns null when:
 *   - the global kill switch is off (spritesEnabled() === false)
 *   - pokedex_id is null / undefined / non-positive
 *
 * Callers can therefore short-circuit unconditionally:
 *   const url = spriteUrlFor(pokedex_id);
 *   if (!url) return null;
 */
export function spriteUrlFor(
  pokedexId: number | null | undefined,
  opts: { source?: SpriteSource } = {},
): string | null {
  if (!spritesEnabled()) return null;
  if (typeof pokedexId !== "number" || pokedexId <= 0) return null;
  const source = opts.source ?? spriteSource();
  if (source === "leekduck") {
    return `https://cdn.leekduck.com/assets/img/pokemon_icons_crop/pm${pokedexId}.icon.png`;
  }
  // PokeAPI default.
  return `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${pokedexId}.png`;
}
